import { ASPECT_RATIOS, outputTypes } from "@/constants/Inputs";
import { defineStore } from "pinia";
import { v4 as uuidv4 } from "uuid";
import { markRaw } from "vue";
import { LANGUAGES } from "@/constants/Inputs";
import { useAppStore } from "./app";

interface GenerateState {
  // New fields for creative generation
  prompt: string;
  brandName: string;
  toneId: number;
  styleId: number;
  layoutId: number;
  // The picked guideline layout's id, used instead of layoutId when the brand has guideline layouts.
  guidelineLayoutId: string | null;
  resolutionId: number;
  languageId: string;
  themeId: number,
  creativeUrl: string;
  isGeneratingCreative: boolean;
  hasError: boolean;
  selectedColors: string[];
  aspectRatio: string;
  creativeTemplateId: number | null;
  liteTemplateId: number | null;
  layerTemplateId: number | null;
  isPromptOptimizationEnabled: boolean;
  outputType: string;
  uniqueId: string;
  scaleX: number
  scaleY: number
  layers: any[]
  finalLayerData: any
  width: number,
  height: number,

  // NEW FIELDS
  activeLayerMap: Map<string, any>
  hasAutoDetected: boolean
  mergeModeActive: boolean
  isLayersVisible: boolean;
  promptAttachedImageUrl: string | null;
}

export const useGenerateStore = defineStore("generate", {
  state: (): GenerateState => ({
    // Initialize new fields  
    prompt: "",
    brandName: "",
    toneId: 4,
    styleId: 1,
    layoutId: 1,
    guidelineLayoutId: null,
    resolutionId: 2,
    languageId: LANGUAGES[0].id,
    themeId: 1,
    creativeUrl: "",
    isGeneratingCreative: false,
    hasError: false,
    selectedColors: [],
    aspectRatio: ASPECT_RATIOS[1].id,
    creativeTemplateId: null,
    liteTemplateId: null,
    layerTemplateId: null,
    isPromptOptimizationEnabled: true,
    outputType: outputTypes.ad,
    uniqueId: "",
    scaleX: 1,
    scaleY: 1,
    layers: [],
    finalLayerData: null,
    width: 1024,
    height: 1024,

    // NEW
    activeLayerMap: new Map(),
    hasAutoDetected: false,
    mergeModeActive: false,
    isLayersVisible: false,
    promptAttachedImageUrl: null,
  }),
  getters: {
    getActiveLayerCount: (state) => {
      return state.activeLayerMap.size
    },
    hasActiveLayers: (state) => {
      return state.activeLayerMap.size > 0;
    },
  },
  actions: {
    // Manage fields for creative generation
    setPrompt(prompt: string) {
      this.prompt = prompt;
    },
    setBrandName(name: string) {
      this.brandName = name;
    },
    setToneId(id: number) {
      this.toneId = id;
    },
    setStyleId(id: number) {
      this.styleId = id;
    },
    setLayoutId(id: number) {
      this.layoutId = id;
    },
    setGuidelineLayoutId(id: string | null) {
      this.guidelineLayoutId = id;
    },
    setResolutionId(id: number) {
      this.resolutionId = id;
    },
    setLanguageId(id: string) {
      this.languageId = id;
    },
    setDefaultLanguageForBusinessProfile(bpId: number) {
      if (bpId === 19901) {
        this.languageId = LANGUAGES[1].id;
      } else if (bpId === 19902) {
        this.languageId = LANGUAGES[2].id;
      } else {
        this.languageId = LANGUAGES[0].id;
      }
    },
    setAspectRatio(ratio: string) {
      this.aspectRatio = ratio;
    },
    setThemeId(id: number){
      this.themeId = id;
    },
    setCreativeUrl(url: string) {
      this.creativeUrl = url;
    },
    setSelectedColors(colors: string[]) {
      this.selectedColors = colors;
    },
    setCreativeTemplateId(id: number | null) {
      this.creativeTemplateId = id;
    },
    setLiteTemplateId(id: number | null) {
      this.liteTemplateId = id;
    },
    setLayerTemplateId(id: number | null) {
      this.layerTemplateId = id;
    },
    setIsPromptOptimizationEnabled(val: boolean) {
      this.isPromptOptimizationEnabled = val;
    },
    setOutputType(type: string) {
      this.outputType = type;
    },
    setHasError(val: boolean) {
      this.hasError = val;
    },
    setIsCreativeGenerating(val: boolean) {
      this.isGeneratingCreative = val;
    },
    addLayers(newLayers: []) {
      if (!newLayers || !Array.isArray(newLayers) || newLayers.length === 0) return;
      this.layers.push(...newLayers)
    },
    setLayers(newLayers: []) {
      this.layers = newLayers
    },
    /** ACTIVE LAYER MANAGEMENT **/
    addActiveLayer(id: string, fabricObj: any) {
      this.activeLayerMap.set(id, markRaw(fabricObj));
    },
    removeActiveLayer(id: string) {
      this.activeLayerMap.delete(id);
    },
    resetLayers() {
      this.layers = [];
    },
    resetActiveLayerMap() {
      this.activeLayerMap = new Map(); // or this.activeLayerMap.clear() for reactive-safe
    },
    reset() {
      this.prompt = "";
      this.brandName = "";
      this.toneId = 4;
      this.styleId = 1;
      this.layoutId = 1;
      this.guidelineLayoutId = null;
      this.themeId = 1;
      //TODO: Will need to change this to be dynamic based on the selected business profile
      const appStore = useAppStore();
      if(appStore.selectedBusinessProfileId) {
        this.setDefaultLanguageForBusinessProfile(appStore.selectedBusinessProfileId);
      }
      this.creativeUrl = "";
      this.isGeneratingCreative = false;
      this.hasError = false;
      this.selectedColors = [];
      this.aspectRatio = ASPECT_RATIOS[1].id;
      this.creativeTemplateId = null;
      this.liteTemplateId = null;
      this.layerTemplateId = null;
      this.isPromptOptimizationEnabled = true;
      this.outputType = outputTypes.ad;
      this.uniqueId = "";
      this.scaleX = 1
      this.scaleY = 1
      this.layers = []
      this.activeLayerMap = new Map();
      this.width = 1024;
      this.height = 1024;
      this.hasAutoDetected = false;
      this.mergeModeActive = false;
      this.isLayersVisible = false;
      this.promptAttachedImageUrl = null;
    },
    setUniqueId(id?: string) {
      if (id) {
        this.uniqueId = id;
        return;
      }
      this.uniqueId = uuidv4();
    },
    setHasAutoDetected(val: boolean) {
      this.hasAutoDetected = val;
    },
    setMergeModeActive(val: boolean) {
      this.mergeModeActive = val;
    },
    setPromptAttachedImageUrl(url: string | null) {
      this.promptAttachedImageUrl = url;
    },
    setIsLayersVisible(val: boolean) {
      this.isLayersVisible = val;
    },
    resetLayerState() {
      this.hasAutoDetected = false;
      this.mergeModeActive = false;
      this.isLayersVisible = false;
      this.layers = [];
      this.activeLayerMap.clear();
      this.layerTemplateId = null;
    },
    
  },
});
